import { DAY, UserAction } from "@brookbush/types";

import { useUser } from "@clerk/nextjs";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";

import Button from "./Button";
import { useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

interface CommentEditorProps {
	success: {
		label: string;
		onClick: (value: string) => void;
	};

	failure?: {
		label: string;
		onClick: () => void;
	};

	initialValue?: string;
}

const CommentEditor = ({
	success,
	initialValue = "",
	failure,
}: CommentEditorProps) => {
	const { user, isSignedIn } = useUser();
	const { setUserAction } = useUserAction();
	const router = useRouter();
	const cookieKey = `comments[${router.asPath.split(/[?#]/)[0] || ""}]`;
	const [value, setValue] = useState(initialValue);
	const trackCTA = useTrackCTA();

	useEffect(() => {
		(async () => {
			const storedComment = await getCookie(cookieKey);
			if (storedComment) setValue(storedComment);
		})();
	}, [cookieKey]);

	useDebounce(
		() => {
			if (value === "") {
				deleteCookie(cookieKey);
			} else {
				setCookie(
					cookieKey,
					value,
					// Cookies are in seconds not milliseconds so divide by 1000
					{ maxAge: (7 * DAY) / 1000 },
				);
			}
		},
		500,
		[value],
	);

	return (
		<div className="flex gap-4">
			<form
				className="flex w-full flex-col items-end gap-3"
				onSubmit={(e) => {
					e.preventDefault();

					success.onClick(value);

					setValue("");
					deleteCookie(`comments[${router?.asPath?.split(/[?#]/)[0] || ""}]`);
				}}
			>
				<div className="flex w-full flex-col gap-2 rounded-xl rounded-tl-none border border-slate-300 px-4 py-3 ring-sky-100 transition-shadow focus-within:border-primary-400 focus-within:ring-4 dark:border-slate-600 dark:ring-sky-400/40 dark:focus-within:border-sky-400">
					{<h3 className="font-medium">{user?.fullName || "Guest"}</h3>}

					<textarea
						rows={3}
						onInput={(e) => {
							e.currentTarget.style.height = "";
							e.currentTarget.style.height =
								e.currentTarget.scrollHeight + "px";
						}}
						className="w-full resize-y border-none bg-transparent p-0 shadow-none focus:ring-0 dark:bg-transparent"
						placeholder="Start typing here"
						value={value}
						onChange={(e) => setValue(e.target.value)}
					/>
				</div>

				<div className="flex gap-2">
					{failure && (
						<Button
							onClick={failure.onClick}
							variant={"outlined"}
							disabled={!isSignedIn}
						>
							{failure.label}
						</Button>
					)}
					<Button
						disabled={!value.length}
						onClick={() => {
							if (!isSignedIn) {
								trackCTA(UserAction.SIGN_UP, "Add Comment");

								setUserAction(UserAction.SIGN_UP);
							}
						}}
						type={!isSignedIn ? "button" : "submit"}
					>
						{success.label}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default CommentEditor;
